import React from "react";

import { IconName } from "../common/Icon";
import {
    GroupChunk,
    GroupHeading,
    GroupItem,
    GroupList,
    renderIcon,
} from "./Group.styles";
import { ItemButton } from "./ItemButton";
import { ItemLink } from "./ItemLink";
import { isPickerButton, isPickerLink, PickerItem } from "./Picker.types";

interface IResultGroupProps<TTab extends string, TGroup extends string> {
    heading: { label: string; icon?: IconName };
    hideHeading: boolean;
    indexedItem: PickerItem<TTab, TGroup> | null;
    setIndexedItem: (item: PickerItem<TTab, TGroup>) => void;
    showBadges?: boolean;
    onSelectItem?: (item: PickerItem<TTab, TGroup>) => void;
    children: Set<PickerItem<TTab, TGroup>>;
}

const itemKeys = new WeakMap<PickerItem<any, any>, string>();
function getItemKey<TTab extends string, TGroup extends string>(
    item: PickerItem<TTab, TGroup>,
): string | number {
    if (item.id) {
        return item.id;
    }

    let key = itemKeys.get(item);
    if (!key) {
        key = Math.random().toString(36).substring(2);
        itemKeys.set(item, key);
    }
    return key;
}

export function ResultGroup<TTab extends string, TGroup extends string>(
    props: IResultGroupProps<TTab, TGroup>,
) {
    const {
        children,
        heading,
        hideHeading,
        indexedItem,
        setIndexedItem,
        showBadges = true,
        onSelectItem,
    } = props;

    const renderedItems = [...children.values()].map(item => (
        <GroupItem key={getItemKey(item)}>
            {isPickerLink(item) ? (
                <ItemLink
                    item={item}
                    selected={indexedItem === item}
                    setIndexedItem={setIndexedItem}
                    onSelect={onSelectItem}
                />
            ) : isPickerButton(item) ? (
                <ItemButton
                    item={item}
                    showBadge={showBadges}
                    selected={indexedItem === item}
                    setIndexedItem={setIndexedItem}
                    onSelect={onSelectItem}
                />
            ) : null}
        </GroupItem>
    ));

    const renderedList = <GroupList>{renderedItems}</GroupList>;

    return (
        <GroupChunk>
            {!hideHeading && (
                <GroupHeading>
                    {renderIcon(heading.icon)}
                    {heading.label}
                </GroupHeading>
            )}
            {renderedList}
        </GroupChunk>
    );
}
